import { useMemo } from "react";
import { List } from "lucide-react";

import { cn } from "@/modern/utils";

import { getNotificationCategory } from "../constants/config";
import type { MappedNotificationCounts, NotificationFilter } from "../types";
import type { UpdatesInboxMode } from "../utils/timeWindow";
import { NotificationCaughtUpEmpty } from "./NotificationCaughtUpEmpty";
import { NotificationTileButton } from "./NotificationTileButton";

function distinctCaption(label: string, caption: string | undefined): string | undefined {
  const trimmed = caption?.trim();
  if (!trimmed) return undefined;
  if (trimmed.toLowerCase() === label.trim().toLowerCase()) return undefined;
  return trimmed;
}

/** Category tiles shown on Routine (count > 0). */
export function visibleRoutineCategories(mappedCounts: MappedNotificationCounts) {
  return mappedCounts.categories.filter((c) => c.count > 0);
}

export function NotificationCategoriesView({
  mappedCounts,
  expanded = false,
  onSelectCategory,
  onBack,
  inboxMode = "unread",
  onSeeEarlier,
}: {
  mappedCounts: MappedNotificationCounts;
  /** Expanded shell — three-column category grid. */
  expanded?: boolean;
  onSelectCategory: (
    filter: Extract<NotificationFilter, { kind: "category" }>,
    label: string,
  ) => void;
  onBack: () => void;
  inboxMode?: UpdatesInboxMode;
  onSeeEarlier?: () => void;
}) {
  const categoriesWithCounts = useMemo(
    () => visibleRoutineCategories(mappedCounts),
    [mappedCounts],
  );

  if (categoriesWithCounts.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col px-5 pb-4 pt-3 sm:px-7">
        <NotificationCaughtUpEmpty
          eyebrow="Routine"
          caption="No categories with updates right now. Go back when you're ready."
          onBack={onBack}
          inboxMode={inboxMode}
          onSeeEarlier={onSeeEarlier}
          classes={{ bg: "bg-amber-500", text: "text-white", ring: "ring-amber-500/20" }}
          icon={List}
        />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-5 sm:p-7">
      <div
        className={cn(
          "grid grid-cols-1 gap-3",
          expanded ? "sm:grid-cols-3" : "sm:grid-cols-2",
        )}
      >
        {categoriesWithCounts.map((category) => {
          const meta = getNotificationCategory(category.value);
          return (
            <NotificationTileButton
              key={category.value}
              onClick={() =>
                onSelectCategory(
                  { kind: "category", category: category.value },
                  category.label,
                )
              }
              span="col-span-1 min-h-[9rem]"
              className="p-5"
              classes={category.classes}
              eyebrow={category.label}
              eyebrowClassName="text-xs tracking-[0.16em]"
              value={String(category.count)}
              caption={distinctCaption(category.label, meta?.caption)}
              captionClassName="text-base font-normal leading-snug text-foreground/80"
              icon={category.icon}
            />
          );
        })}
      </div>
    </div>
  );
}
